import { Spinner } from '@fluentui/react-components';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { logger } from '../lib/logger';

interface OfficeContextValue {
  isReady: boolean;
  host: Office.HostType | null;
  isOfficeHost: boolean;
}

const OfficeContext = createContext<OfficeContextValue>({
  isReady: false,
  host: null,
  isOfficeHost: false,
});

export function OfficeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OfficeContextValue>({
    isReady: false,
    host: null,
    isOfficeHost: false,
  });

  useEffect(() => {
    // Plain browser mode. Do not render the real taskpane here; the add-in is
    // intended to run inside Outlook where Office can provide mailbox/dialog APIs.
    if (typeof Office === 'undefined') {
      setState({ isReady: true, host: null, isOfficeHost: false });
      return;
    }
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      logger.warn('[office] Office.onReady timed out; continuing with available Office.context');
      const host = Office.context?.host ?? null;
      setState({ isReady: true, host, isOfficeHost: host === Office.HostType.Outlook });
    }, 5000);

    Office.onReady((info) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      setState({
        isReady: true,
        host: info.host ?? null,
        isOfficeHost: info.host === Office.HostType.Outlook,
      });
    });

    return () => window.clearTimeout(timeout);
  }, []);

  if (!state.isReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner label="Loading Office context..." />
      </div>
    );
  }

  return <OfficeContext.Provider value={state}>{children}</OfficeContext.Provider>;
}

export function useOffice() {
  return useContext(OfficeContext);
}
